import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@guryou/html-components';
import { stateIsLoaded, useEntityTypes } from '@guryou/redux';
import { parseAsBool, printPrice, printDate } from '@guryou/utils';

const PaymentHistory = ({ myOrders }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const entityTypeOptions = useEntityTypes({ showAll: true }) || [];

  // Filter and paginate data - must be before any conditional returns
  const filteredData = useMemo(() => {
    if (!stateIsLoaded(myOrders) || !myOrders.data) {
      return [];
    }

    let result = myOrders.data || [];

    if (typeFilter) {
      result = result.filter(x => x.type === typeFilter);
    }

    if (search) {
      result = result.filter(x => x.name && x.name.toLowerCase().includes(search.toLowerCase()));
    }

    return result;
  }, [myOrders, typeFilter, search]);

  if (!stateIsLoaded(myOrders)) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Download CSV
  const handleDownload = () => {
    const headers = [t('PROFILE.item'), t('PROFILE.date'), t('PROFILE.status'), t('PROFILE.price'), t('PROFILE.type')];
    const rows = filteredData.map(item => [
      item.name || '',
      item.date ? printDate(item.date) : '',
      parseAsBool(item.paid) ? t('PROFILE.paid') : t('PROFILE.unpaid'),
      printPrice(item.price),
      item.type ? item.type.charAt(0).toUpperCase() + item.type.slice(1) : '',
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `GURYOU_${t('PROFILE.paymentHistory')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{t('PROFILE.paymentHistory')}</h2>
          <button
            onClick={handleDownload}
            className={`inline-flex items-center gap-2 px-4 py-2 bg-[var(--feel-primary)] text-white rounded-lg hover:opacity-90 transition-colors`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {t('GENERAL.download')} CSV
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
          <div className="flex flex-col">
            <label htmlFor="search" className="block mb-2 text-sm font-medium text-gray-900">
              {t('GENERAL.search')}
            </label>
            <input
              type="text"
              id="search"
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder={t('PROFILE.searchByItem')}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:outline-none transition-colors"
              style={{
                '--focus-ring-color': 'var(--feel-primary)',
              }}
              onFocus={e => {
                e.target.style.borderColor = 'var(--feel-primary)';
                e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--feel-primary) 25%, transparent)`;
              }}
              onBlur={e => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '';
              }}
            />
          </div>
          <div className="flex flex-col">
            <label htmlFor="type" className="block mb-2 text-sm font-medium text-gray-900">
              {t('PROFILE.type')}
            </label>
            <select
              id="type"
              value={typeFilter}
              onChange={e => {
                setTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg focus:outline-none transition-colors"
              style={{
                '--focus-ring-color': 'var(--feel-primary)',
              }}
              onFocus={e => {
                e.target.style.borderColor = 'var(--feel-primary)';
                e.target.style.boxShadow = `0 0 0 2px color-mix(in srgb, var(--feel-primary) 25%, transparent)`;
              }}
              onBlur={e => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '';
              }}
            >
              <option value="">{t('GENERAL.all')}</option>
              {entityTypeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {filteredData.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">{t('GENERAL.noPaymentsHistory')}</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.item')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.date')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.price')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('PROFILE.type')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedData.map((item, index) => (
                    <tr key={item.id || index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name || t('GENERAL.noName')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.date ? printDate(item.date) : '/'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            parseAsBool(item.paid) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {parseAsBool(item.paid) ? t('PROFILE.paid') : t('PROFILE.unpaid')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{printPrice(item.price)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {item.type ? item.type.charAt(0).toUpperCase() + item.type.slice(1) : '/'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  {t('GENERAL.showing', 'Showing')} {startIndex + 1} {t('GENERAL.to', 'to')} {Math.min(endIndex, filteredData.length)}{' '}
                  {t('GENERAL.of', 'of')} {filteredData.length} {t('GENERAL.results', 'results')}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('GENERAL.previous', 'Previous')}
                  </button>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('GENERAL.next', 'Next')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
